import React, { useId, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { NothingUrgent } from './NothingUrgent';
import { cx, focusRing, motion } from './classes';

const badge = 'w-8 h-8 flex-none rounded-full inline-flex items-center justify-center font-mono text-data-sm';

/**
 * One step of a fix: the plugin, where it goes and what it does, and its settings as [label, value]
 * pairs. Each move opens and closes on its own.
 */
export const PluginMove = ({ number, plugin, target, purpose, settings = [], defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = useId();
  const where = [target, purpose].filter(Boolean).join(' · ');
  const heading = (
    <span className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-baseline gap-x-space-3 font-text">
      <span className="text-body-strong text-ink">{plugin}</span>
      {where && <span className="text-small text-muted">{where}</span>}
    </span>
  );

  // A move with nothing to set has nothing to open
  if (settings.length === 0) {
    return (
      <div className="min-h-[56px] px-space-4 py-space-2 flex items-center gap-space-3 rounded-radius-md bg-base">
        <span className={cx(badge, 'bg-raised text-ink')}>{number}</span>
        {heading}
      </div>
    );
  }

  return (
    <div className="rounded-radius-md bg-base">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen(!open)}
        className={cx('w-full min-h-[56px] px-space-4 py-space-2 flex items-center gap-space-3 text-left rounded-radius-md bg-transparent', focusRing)}
      >
        {/* Open: ink badge. Closed: raised. Never coral */}
        <span className={cx(badge, open ? 'bg-ink text-on-light' : 'bg-raised text-ink')}>{number}</span>
        {heading}
        <ChevronDown
          size={18}
          strokeWidth={2.5}
          aria-hidden="true"
          className={cx('flex-none text-muted transition-transform', motion, !open && '-rotate-90')}
        />
      </button>
      <dl
        id={panelId}
        hidden={!open}
        className={cx(open && 'grid', 'm-0 grid-cols-2 sm:grid-cols-3 gap-space-3 px-space-4 pb-space-4 sm:pl-[60px]')}
      >
        {settings.map(([label, value]) => (
          <div key={label} className="flex flex-col gap-0.5">
            <dt className="font-text text-caption text-muted">{label}</dt>
            <dd className="m-0 font-mono text-data text-ink">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

/**
 * The one thing to fix next: a short `title`, one sentence on `why`, and the ordered plugin `moves`
 * ({ plugin, target, purpose, settings }). With no title there's nothing to fix, so it shows
 * Nothing urgent instead.
 */
export const FixNext = ({ title, why, moves = [] }) => {
  const headingId = useId();
  if (!title) return <NothingUrgent />;

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-space-3">
      <div className="flex flex-col gap-space-1">
        {/* The only coral in this block */}
        <span className="font-text text-label text-coral">Fix next</span>
        <h3 id={headingId} className="m-0 font-display text-title-3 text-ink">{title}</h3>
        {why && <p className="m-0 font-text text-body text-ink-soft">{why}</p>}
      </div>
      {moves.length > 0 && (
        <>
          <span className="mt-space-1 font-text text-small text-muted">Your plugin moves · do them in this order</span>
          <ol className="m-0 p-0 list-none flex flex-col gap-space-3">
            {moves.map((move, index) => (
              <li key={`${index}-${move.plugin}`}>
                <PluginMove number={index + 1} defaultOpen={index === 0} {...move} />
              </li>
            ))}
          </ol>
        </>
      )}
    </section>
  );
};
